import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useLocation, useRoute } from 'wouter';
import { Helmet } from 'react-helmet-async';
import { usePasswordReset } from '@/hooks/use-profile';
import { profileService } from '@/services/api/profile.service';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { FormField } from '@/components/ui/form-field';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Spinner } from '@/components/ui/spinner';
import { passwordResetSchema, type PasswordResetFormData } from '@/schemas/profile.schema';
import { Lock, AlertCircle, CheckCircle } from 'lucide-react';
import { LanguagePill } from '@/components/chrome/LanguagePill';

export default function ResetPasswordPage() {
  const [, params] = useRoute<{ token: string }>('/reset-password/:token');
  const [, setLocation] = useLocation();
  const [isValidating, setIsValidating] = useState(true);
  const [isValid, setIsValid] = useState(false);
  const [validationMessage, setValidationMessage] = useState('');
  const { resetPassword, isResetting, resetSuccess } = usePasswordReset();

  const token = params?.token || '';

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch
  } = useForm<PasswordResetFormData>({
    resolver: zodResolver(passwordResetSchema)
  });

  const newPassword = watch('newPassword');

  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setIsValid(false);
        setValidationMessage('No reset token provided');
        setIsValidating(false);
        return;
      }

      try {
        const result = await profileService.validateResetToken(token);
        setIsValid(result.valid);
        setValidationMessage(result.message || '');
      } catch (error) {
        setIsValid(false);
        setValidationMessage('Failed to validate reset token');
      } finally {
        setIsValidating(false);
      }
    };

    validateToken();
  }, [token]);

  const onSubmit = async (data: PasswordResetFormData) => {
    await resetPassword({
      token,
      newPassword: data.newPassword,
      passwordConfirmation: data.passwordConfirmation
    });
  };

  const handleReturnToLogin = () => {
    setLocation('/login');
  };

  // Loading state
  if (isValidating) {
    return (
      <div className="container max-w-md mx-auto py-12 px-4">
        <div className="flex justify-end mb-4">
          <LanguagePill />
        </div>
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col items-center justify-center py-12 space-y-4">
              <Spinner className="w-8 h-8" />
              <p className="text-soft">Validating reset link...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Invalid token
  if (!isValid) {
    return (
      <div className="container max-w-md mx-auto py-12 px-4">
        <div className="flex justify-end mb-4">
          <LanguagePill />
        </div>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-destructive">
              <AlertCircle className="w-5 h-5" />
              Invalid Reset Link
            </CardTitle>
            <CardDescription>
              This password reset link is invalid or has expired
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert variant="destructive">
              <AlertDescription>
                {validationMessage || 'The reset link may have expired or already been used.'}
              </AlertDescription>
            </Alert>

            <div className="space-y-2">
              <Button onClick={handleReturnToLogin} className="w-full">
                Return to Login
              </Button>
              <Button
                variant="outline"
                onClick={() => setLocation('/forgot-password')}
                className="w-full"
              >
                Request New Reset Link
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Success state
  if (resetSuccess) {
    return (
      <div className="container max-w-md mx-auto py-12 px-4">
        <div className="flex justify-end mb-4">
          <LanguagePill />
        </div>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center space-y-6 py-6">
              <div className="flex justify-center">
                <div className="rounded-full bg-green-100 p-3">
                  <CheckCircle className="w-12 h-12 text-green-600" />
                </div>
              </div>

              <div className="space-y-2">
                <h3 className="text-h1 text-ink">Password Reset!</h3>
                <p className="text-soft">
                  Your password has been successfully reset.
                </p>
                <p className="text-soft text-sm">
                  You can now log in with your new password.
                </p>
              </div>

              <Button onClick={handleReturnToLogin} className="w-full">
                Go to Login
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Reset form
  return (
    <div className="container max-w-md mx-auto py-12 px-4">
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div className="flex justify-end mb-4">
        <LanguagePill />
      </div>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lock className="w-5 h-5" />
            Reset Your Password
          </CardTitle>
          <CardDescription>
            Enter your new password below
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              label="New Password"
              error={errors.newPassword?.message}
            >
              <Input
                id="newPassword"
                type="password"
                placeholder="Enter new password"
                {...register('newPassword')}
                autoFocus
              />
            </FormField>
            {newPassword && (
              <div className="text-xs space-y-1 -mt-2">
                <p className={newPassword.length >= 8 ? 'text-success' : 'text-soft'}>
                  ✓ At least 8 characters
                </p>
                <p className={/[A-Z]/.test(newPassword) ? 'text-success' : 'text-soft'}>
                  ✓ One uppercase letter
                </p>
                <p className={/[a-z]/.test(newPassword) ? 'text-success' : 'text-soft'}>
                  ✓ One lowercase letter
                </p>
                <p className={/[0-9]/.test(newPassword) ? 'text-success' : 'text-soft'}>
                  ✓ One number
                </p>
              </div>
            )}

            <FormField
              label="Confirm New Password"
              error={errors.passwordConfirmation?.message}
            >
              <Input
                id="passwordConfirmation"
                type="password"
                placeholder="Confirm new password"
                {...register('passwordConfirmation')}
              />
            </FormField>

            <Button type="submit" className="w-full" disabled={isResetting}>
              {isResetting ? 'Resetting Password...' : 'Reset Password'}
            </Button>

            <Button
              type="button"
              variant="ghost"
              className="w-full"
              onClick={handleReturnToLogin}
            >
              Cancel
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
